/**
 * Copying things out of an answer.
 *
 * Two needs, both constant while working a problem set: the LaTeX of one
 * equation, and the whole answer as Markdown to paste into notes. Both must
 * preserve the source, not the rendering — pasting `∇·E = ρ/ε₀` into Obsidian
 * is useless; pasting `$\nabla \cdot E = \rho/\epsilon_0$` is not.
 */

/**
 * The TeX source of a rendered equation.
 *
 * KaTeX keeps the original in a MathML `<annotation>` precisely so it survives
 * a copy, which is what makes this exact rather than a re-serialisation.
 */
export function equationSource(node: Element): string | null {
  const annotation = node.querySelector(
    'annotation[encoding="application/x-tex"]',
  );
  const tex = annotation?.textContent?.trim();
  if (!tex) return null;

  // Display equations paste as display equations.
  return node.classList.contains("katex-display") ||
    node.closest(".katex-display") !== null
    ? `$$\n${tex}\n$$`
    : `$${tex}$`;
}

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return legacyCopy(text);
  }
}

/**
 * Fallback for a browser that refuses the async clipboard — over plain HTTP
 * outside localhost, `navigator.clipboard` is simply undefined.
 */
function legacyCopy(text: string): boolean {
  try {
    const staging = document.createElement("textarea");
    staging.value = text;
    staging.setAttribute("readonly", "");
    staging.style.position = "fixed";
    staging.style.opacity = "0";
    document.body.appendChild(staging);
    staging.select();

    const copied = document.execCommand("copy");
    staging.remove();
    return copied;
  } catch {
    return false;
  }
}
